import rules from './shared/state-province-rules.json';

export const normalizeStateText = value => (typeof value === 'string' ? value : '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

export const isStateReference = data => data !== null && typeof data === 'object'
    && !Array.isArray(data) && Object.values(data).some(values => Array.isArray(values) && values.length)
    && Object.values(data).every(values => Array.isArray(values)
        && values.every(value => typeof value === 'string' && value.trim()));

export const referenceCountry = (country, reference) => {
    if (reference?.[country]?.length) return country;
    return rules.countryAliases[country] || country;
};

export const getStateOptions = (country, reference) =>
    [...new Set(reference?.[referenceCountry(country, reference)] || [])];

export const isStateProvinceRequired = country =>
    rules.requiredStateProvinceCountries.includes(rules.countryAliases[country] || country);

export const matchAllowedState = (value, options, country) => {
    if (options.includes(value)) return value;
    const normalized = normalizeStateText(value);
    const matches = [...new Set(options.filter(option => normalizeStateText(option) === normalized))];
    if (matches.length === 1) return matches[0];
    if (matches.length > 1) return null;
    if (country === 'United States' && typeof value === 'string') {
        const fullName = rules.usPostalCodes[value.trim().toUpperCase()];
        if (options.includes(fullName)) return fullName;
    }
    return null;
};

export const validateStateProvince = (record, reference, status = 'ready') => {
    const country = record.Country;
    const value = record.StateProvince;
    const valid = result => ({ valid: true, value: result, reason: null, message: '' });
    const invalid = (reason, message) => ({ valid: false, value, reason, message });
    if (country != null && typeof country !== 'string') {
        return invalid('invalid_country', 'Country must be text.');
    }
    if (!country || !country.trim()) return valid(value);
    if (status !== 'ready' || !isStateReference(reference)) {
        return invalid('reference_unavailable', 'State/province options are unavailable. Retry loading them before saving.');
    }
    if (value != null && typeof value !== 'string') {
        return invalid('invalid_type', 'State/province must be text.');
    }
    const options = getStateOptions(country, reference);
    if (!value || !value.trim()) {
        return isStateProvinceRequired(country)
            ? invalid('required', `${options.length ? 'Select' : 'Enter'} a state/province for ${country}.`)
            : valid(value);
    }
    if (!options.length) return valid(value);
    const matched = matchAllowedState(value, options, referenceCountry(country, reference));
    return matched === null
        ? invalid('not_allowed', `Select an allowed state/province for ${country}.`)
        : valid(matched);
};

export const changeCountry = (record, country) => ({
    ...record,
    Country: country,
    StateProvince: record.Country === country ? record.StateProvince : ''
});

export const validationErrorMessage = (error, fallback = 'Unable to save. Please try again.') => {
    const data = error?.response?.data;
    if (!['STATE_PROVINCE_INVALID', 'STATE_REFERENCE_UNAVAILABLE'].includes(data?.code)) return fallback;
    const details = (data.errors || []).map(item => {
        const id = item.DraftID ? `Draft ${item.DraftID}: ` : item.NID ? `NID ${item.NID}: ` : '';
        return `${id}${item.message || `Select a valid state/province for ${item.country}.`}`;
    });
    return [data.message, ...details].filter(Boolean).join(' ');
};
